import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Alert } from '@ui-component-library/components/Alert';
import { Button } from '@ui-component-library/components/Button';
import { Icon } from '@ui-component-library/components/Icon';
import type { IconName } from '@ui-component-library/components/Icon/Icon.types';
import { Text } from '@ui-component-library/components/Text';

import {
  fileInputClasses,
  fileRemoveIconClasses,
  paddingDivClasses,
  paddingLeftDivClasses,
  rowClasses,
  uploadedContainerClasses,
  uploadedFileClasses,
  uploadedFileLeftClasses,
  wrapperClasses
} from './FileUpload.styles';
import type { FileItem, FileUploadProps } from './FileUpload.types';

const getIconFromName = (filename: string) => {
  const file_ext: string = filename.split('.')[1];
  let icon: IconName = 'FileTxtSimple';
  if (['jpg', 'jpeg', 'png'].includes(file_ext)) {
    icon = 'FileImageSimple';
  } else if (['kml', 'geopackage', 'geojson', 'xml'].includes(file_ext)) {
    icon = 'FileMapSimple';
  } else if (['zip'].includes(file_ext)) {
    icon = 'FileZipSimple';
  } else if (['xlsx', 'xls', 'doc', 'docx'].includes(file_ext)) {
    icon = 'FileDocSimple';
  } else if (['pdf'].includes(file_ext)) {
    icon = 'PdfSimple';
  }

  return icon;
};

const formatFileSize = (bytes: number): string =>
  bytes > 500000 ? `${(bytes / 1e6).toFixed(1)} MB` : `${(bytes / 1000).toFixed(1)} KB`;

function FileUpload({
  name,
  label,
  accept,
  multiple = false,
  buttonText,
  onChange,
  idleText = '',
  errorText,
  successText,
  value = [],
  isValid = undefined,
  tabIndex = 0
}: FileUploadProps) {
  const [files, setFiles] = useState<FileItem[]>(value);
  const [valid, setValid] = useState(isValid);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currIdMax = value.reduce((prev, curr) => (curr.id > prev ? curr.id : prev), 0) + 1;
  const [fileIdCounter, setFileIdCounter] = useState(currIdMax);

  /* eslint-disable react-hooks/set-state-in-effect -- Intentional: sync controlled component state with props */
  useEffect(() => {
    setFiles(value);
    setValid(isValid);
  }, [value, isValid]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const alertModel = useMemo(():
    | { text: string; variant: 'bg-cloud-200' | 'bg-success-50' | 'bg-danger-15'; icon: IconName }
    | { text: string }
    | null => {
    if (valid === undefined) {
      return { text: idleText };
    }

    if (valid === true) {
      return { text: successText, variant: 'bg-success-50', icon: 'CheckFilled' };
    }

    if (valid === false) {
      return { text: errorText, variant: 'bg-danger-15', icon: 'ExclamationFilled' };
    }
    if (!errorText) {
      return null;
    }
    return null;
  }, [valid, idleText, successText, errorText]);

  const HandleUploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      if (!multiple) {
        setFiles([]);
        onChange([], name);
      }
      return;
    }
    const newUploadedFiles: FileItem[] = Array.from(e.target.files, (file, i) => ({
      id: fileIdCounter + i,
      name: file.name,
      size: formatFileSize(file.size),
      icon: getIconFromName(file.name),
      file,
      valid: undefined
    }));

    const updatedFiles = multiple ? [...files, ...newUploadedFiles] : newUploadedFiles;
    setFiles(updatedFiles);
    setFileIdCounter(fileIdCounter + e.target.files.length);

    onChange(updatedFiles, name);
  };

  const handleRemoveFiles = async (id: number) => {
    const remainingFiles = files.filter((file) => file.id !== id);
    onChange(remainingFiles, name);
    setFiles(remainingFiles);
    if (remainingFiles.length === 0) {
      setValid(undefined);
    }
  };

  const filesFromFileList = (fileList: FileList | null): FileItem[] => {
    if (!fileList?.length) {
      return [];
    }
    const newUploadedFiles: FileItem[] = Array.from(fileList, (file, i) => ({
      id: fileIdCounter + i,
      name: file.name,
      size: formatFileSize(file.size),
      icon: getIconFromName(file.name),
      file,
      valid: undefined
    }));
    setFileIdCounter(fileIdCounter + fileList.length);
    return newUploadedFiles;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const newItems = filesFromFileList(e.dataTransfer?.files ?? null);
    if (!newItems.length) {
      return;
    }
    const updatedFiles = multiple ? [...files, ...newItems] : newItems;
    setFiles(updatedFiles);
    onChange(updatedFiles, name);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  return (
    <>
      <div
        className={cn(wrapperClasses(), dragging && 'border-[#349269] bg-[#349269]/10')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={tabIndex}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
      >
        <div className={rowClasses()}>
          <Icon name="UploadSimple" size="xlarge" />
        </div>
        <div className={rowClasses()}>
          <Text variant="text-sm/leading-normal/medium">{label}</Text>
        </div>
        <div className={rowClasses()}>
          <Button
            type="button"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            {buttonText}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          onChange={HandleUploadFiles}
          type="file"
          accept={accept}
          multiple={multiple}
          tabIndex={-1}
          name={name}
          className={cn(fileInputClasses(), 'pointer-events-none')}
        />
      </div>
      {alertModel && 'variant' in alertModel && (
        <Alert icon={alertModel.icon} className={alertModel.variant} visible={true}>
          <Text variant="text-sm/leading-normal/medium">{alertModel.text}</Text>
        </Alert>
      )}

      {alertModel && !('variant' in alertModel) && (
        <Text variant="text-sm/leading-normal/medium">{alertModel.text}</Text>
      )}
      <div className={cn(uploadedContainerClasses())}>
        {files.map((file) => {
          return (
            <div className={cn(uploadedFileClasses({ isValid: file.valid }))} key={file.id}>
              <div className={uploadedFileLeftClasses()}>
                <Icon name={file.icon ?? 'FileTxtSimple'} size="xlarge" />

                <div className={paddingLeftDivClasses()}>
                  <Text variant="text-sm/leading-normal/medium">{file.name}</Text>
                  <Text variant="text-xs/leading-normal/medium">{file.size}</Text>
                </div>
              </div>
              {file.valid !== undefined && (
                <div className={fileRemoveIconClasses()} onClick={() => handleRemoveFiles(file.id)}>
                  <Icon name="CrossSimple" />
                  <Text variant="text-2xs/leading-normal/normal">Ta bort</Text>
                </div>
              )}
              {file.valid === undefined && (
                <div className={paddingDivClasses()}>
                  <Text variant="text-2xs/leading-normal/normal">Laddar…</Text>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
export { FileUpload };
